"use client";

import { ReactNode } from "react";

import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";

import { cn, formatIsoStringDate } from "@/lib/utils";
import { t } from "@/lib/localization";

import { GetFilesResponseType } from "../../hooks/useGetFiles";
import { useGetLomdaV2 } from "@/features/storage/sheets/lomdaV2Sheet/hooks/useGetLomdaV2";

import {
    LomdaContent,
    LomdaMetadata,
} from "@/features/lomdaEditorV2/lib/lomdaTypes";
import LomdaV2Thumbnail from "@/features/lomdaEditorV2/utils/lomdaV2Thumbnail";

interface LomdaV2CardProps {
    file: GetFilesResponseType["data"][0];
    onClick?: () => void;
    options?: {
        component: ReactNode;
        positionClassName?: string;
    };
    selected?: boolean;
}

export default function LomdaV2Card({
    file,
    onClick,
    options,
    selected,
}: LomdaV2CardProps) {
    const { data: lomdaData } = useGetLomdaV2(true, file.id);
    const lomda = lomdaData?.data;

    const content: LomdaContent | null = lomda?.content
        ? JSON.parse(lomda.content)
        : null;
    const metadata: LomdaMetadata | null = lomda?.metadata
        ? JSON.parse(lomda.metadata)
        : null;

    const badgeText =
        file.detailForBadge <= 1
            ? t("storage.lomda-card.num-of-scenes-badge.single")
            : file.detailForBadge +
              " " +
              t("storage.lomda-card.num-of-scenes-badge.multiple");

    const title = file.name || t("storage.no-name", undefined, undefined);
    const subtitle = `${t("storage.lomda-v2", undefined, undefined)} · ${formatIsoStringDate(file.date)}`;

    return (
        <Card
            onClick={onClick}
            className={cn(
                "relative group cursor-pointer hover:bg-accent transition shadow-md flex flex-col",
                selected && "border-primary"
            )}
        >
            {options && (
                <div
                    className={cn(
                        "absolute z-10 flex",
                        options.positionClassName || "top-2 left-2"
                    )}
                >
                    {options.component}
                </div>
            )}

            <div className="relative aspect-video rounded-t-md border-b overflow-hidden bg-muted">
                <LomdaV2Thumbnail
                    content={content}
                    metadata={metadata}
                    lomdaId={file.id}
                    lomdaName={file.name || ""}
                    userId={lomda?.userId ?? ""}
                />

                <Badge className="absolute bottom-2 left-2 font-normal px-2">
                    {badgeText}
                </Badge>
            </div>

            <footer className="flex flex-row gap-2 px-3 py-4">
                <div className="flex flex-col truncate gap-2">
                    <h3 className="font-medium truncate" title={title}>
                        {title}
                    </h3>
                    <p className="text-xs text-muted-foreground">{subtitle}</p>
                </div>
            </footer>
        </Card>
    );
}
